// Tank vertex schema
// A storage vessel with liquid at atmospheric or pressurized conditions

import { z } from 'zod';
import { BaseVertexSchema } from '../base/vertex';
import { ValueWithUnitSchema } from '../base/common';

/**
 * Tank Penetration Schema
 * Represents a pipe connection to the tank
 */
export const TankPenetrationSchema = z.object({
  edge_id: z.string(), // ID of connected pipe edge
  edge_name: z.string(), // Name of connected pipe (for display/debug)
  height: ValueWithUnitSchema, // Height of penetration above tank bottom
});

export type TankPenetration = z.infer<typeof TankPenetrationSchema>;

/**
 * Tank Vertex Schema
 * A storage vessel with liquid at atmospheric or pressurized conditions
 * 
 * Uses direct properties (flat structure) since tanks don't have complex modes
 * (ADR-0006: Simple device pattern)
 * 
 * Note: Tanks do not have an "operation" mode - their behavior is determined
 * by liquid level and surface pressure.
 * 
 * @property elevation - Height of the tank bottom (reference point for liquid_level)
 */
// ============================================================================
// v2: Tank Geometry
// ============================================================================

const TankOrientationSchema = z.enum(['no_orientation', 'horizontal', 'vertical']);
const CapPlacementSchema = z.enum(['left_or_right', 'bottom', 'top']);
const CapShapeSchema = z.enum(['flat', 'spherical', 'conical', 'ellipsoid']);

const CapGeometrySchema = z.object({
  cap_shape: CapShapeSchema,
  placement: CapPlacementSchema,
  diameter: ValueWithUnitSchema.optional(),
  length: ValueWithUnitSchema.optional(),
});

const CylindricalBodySchema = z.object({
  diameter: ValueWithUnitSchema,
  length: ValueWithUnitSchema,
});

const CustomGeometryCurvePointSchema = z.object({
  level: ValueWithUnitSchema,
  volume: ValueWithUnitSchema,
});

export const TankGeometrySchema = z.discriminatedUnion('shape', [
  z.object({
    shape: z.literal('spherical'),
    orientation: TankOrientationSchema,
    diameter: ValueWithUnitSchema,
  }),
  z.object({
    shape: z.literal('rectangular'),
    orientation: TankOrientationSchema,
    length: ValueWithUnitSchema,
    width: ValueWithUnitSchema,
    height: ValueWithUnitSchema,
  }),
  z.object({
    shape: z.literal('cylindrical_horizontal'),
    orientation: TankOrientationSchema,
    body: CylindricalBodySchema,
    left_cap: CapGeometrySchema,
    right_cap: CapGeometrySchema,
  }),
  z.object({
    shape: z.literal('cylindrical_vertical'),
    orientation: TankOrientationSchema,
    body: CylindricalBodySchema,
    bottom_cap: CapGeometrySchema,
    top_cap: CapGeometrySchema,
  }),
  z.object({
    shape: z.literal('custom'),
    orientation: TankOrientationSchema,
    curve: z.array(CustomGeometryCurvePointSchema),
  }),
]);

export type TankGeometry = z.infer<typeof TankGeometrySchema>;

// ============================================================================
// Tank Vertex
// ============================================================================

export const TankVertexSchema = BaseVertexSchema.extend({
  type: z.literal('tank'),
  elevation: ValueWithUnitSchema,
  surface_pressure: ValueWithUnitSchema,
  liquid_level: ValueWithUnitSchema,
  penetrations: z.array(TankPenetrationSchema),

  // v2 additions
  fluid_zone_id: z.string().optional(),
  tank_geometry: TankGeometrySchema.optional(),
});

export type TankVertex = z.infer<typeof TankVertexSchema>;

